/**
 * STABILITY-CRITICAL: decide se é seguro apagar os dados locais (saída/limpeza): empurra o que
 * falta, recontabiliza e classifica o que sobrou. Errar aqui = perda de registro de fazenda.
 * Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o responsável pelo sync.
 */
import { PUSHED_TABLES } from '../pendingData'
import type { PendingSummary } from '../pendingData'

// Inverso de SYNC_OBJECT_TO_TABLE (syncWorkerEnhanced.js ~L40) para as tabelas empurradas.
// O teste "drift guard" falha se divergir de `entityTypes` do pushEnhanced.
export const TABLE_TO_ENTITY: Record<(typeof PUSHED_TABLES)[number], string> = {
  farms: 'fazendas',
  corrals: 'retiros',
  protocols: 'protocolos',
  batches: 'lotes',
  d0s: 'd0s',
  dns: 'dns',
  iatfs: 'iatfs',
  farms__rel__users: 'fazendaUsuarios',
  bulls_new: 'touros',
  partida: 'partidas',
}

export const TABLE_LABEL: Record<string, string> = {
  farms: 'fazendas',
  corrals: 'retiros',
  protocols: 'protocolos',
  batches: 'lotes',
  d0s: 'D0',
  dns: 'DN',
  iatfs: 'IATF',
  farms__rel__users: 'vínculos fazenda-usuário',
  bulls_new: 'touros',
  partida: 'partidas',
}

export type GateReason =
  'offline' | 'breaker_open' | 'push_failed' | 'unreadable' | 'not_accepted'

export type DlqItemLite = {
  id: string
  entityType: string
  errorType: string
  lastError: string | null
  attempts: number
  createdAt: number | null
  movedAt: number | null
  entryCount: number | null
}
export type DlqInfo = {
  total: number
  temporary: number
  permanent: number
  unknown: number
  byEntity: Record<string, number>
  items: DlqItemLite[]
}

export function buildDlqInfo(items: any[]): DlqInfo {
  const byEntity: Record<string, number> = {}
  let temporary = 0
  let permanent = 0
  let unknown = 0
  const lite = items.map((i): DlqItemLite => {
    const entity: string = i?.operation?.entityType ?? 'unknown'
    byEntity[entity] = (byEntity[entity] || 0) + 1
    if (i?.errorType === 'temporary') temporary++
    else if (i?.errorType === 'permanent') permanent++
    else unknown++
    return {
      id: i?.id,
      entityType: entity,
      errorType: i?.errorType ?? 'unknown',
      lastError: i?.lastError ?? null,
      attempts: i?.attempts ?? 0,
      createdAt: i?.createdAt ?? null,
      movedAt: i?.movedAt ?? null,
      entryCount: i?.operation?.entryIds?.length ?? null,
    }
  })
  return {
    total: items.length,
    temporary,
    permanent,
    unknown,
    byEntity,
    items: lite,
  }
}

export type BreakerLite = {
  state: string
  lastError: string | null
  lastFailure: string | null
}
export type RefusedInfo = {
  byTable: Record<string, number>
  via: Array<'dlq' | 'http_4xx'>
  lastError: string | null
}
export type GateInput = {
  summary: PendingSummary
  queueByEntity: Record<string, number>
  dlq: DlqInfo
  online: boolean
  breaker: BreakerLite
  roundStartedAt: number
}
export type GateEvaluation =
  | { outcome: 'clear' }
  | { outcome: 'refused'; refused: RefusedInfo }
  | { outcome: 'blocked'; reason: GateReason; tables?: Record<string, number> }

/** sendData não devolve o status; o rastro é `circuitBreaker.getStatus('push').lastError` = 'HTTP 4xx'. */
export function serverRefusedThisRound(b: BreakerLite, since: number): boolean {
  if (!b.lastFailure || !b.lastError) return false
  const at = Date.parse(b.lastFailure)
  return !Number.isNaN(at) && at >= since && /\bHTTP 4\d\d\b/.test(b.lastError)
}

/**
 * Eventos de observabilidade são telemetria interna, não registro de fazenda: não bloqueiam
 * saída nem limpeza (nem a contagem, nem a fonte ilegível). Os de erro vão no diagnostico.json
 * do backup. Mesma regra do contador da barra (`syncStatusLine.ts`).
 */
export const blockingUnreadable = (s: PendingSummary): string[] =>
  s.unreadable.filter((src) => src !== 'events')

export function evaluateGate(i: GateInput): GateEvaluation {
  const s = i.summary
  if (blockingUnreadable(s).length > 0)
    return { outcome: 'blocked', reason: 'unreadable' }
  const dirty = Object.keys(s.dirtyByTable)
    .map((t): [string, number] => [t, s.dirtyByTable[t]])
    .filter(([, n]) => n > 0)
  if (dirty.length === 0) return { outcome: 'clear' }
  if (!i.online) return { outcome: 'blocked', reason: 'offline' }
  if (i.breaker.state === 'OPEN')
    return { outcome: 'blocked', reason: 'breaker_open' }

  const http4xx = serverRefusedThisRound(i.breaker, i.roundStartedAt - 1000)
  const refused: Record<string, number> = {}
  const still: Record<string, number> = {}
  const via: Array<'dlq' | 'http_4xx'> = []
  for (const [table, n] of dirty) {
    const entity = TABLE_TO_ENTITY[table as keyof typeof TABLE_TO_ENTITY]
    const inDlq = !!entity && (i.dlq.byEntity[entity] ?? 0) > 0
    const inQueue = !!entity && (i.queueByEntity[entity] ?? 0) > 0
    if (inDlq) {
      refused[table] = n
      if (!via.includes('dlq')) via.push('dlq')
    } else if (inQueue && http4xx) {
      refused[table] = n
      if (!via.includes('http_4xx')) via.push('http_4xx')
    } else {
      still[table] = n
    }
  }
  if (Object.keys(still).length > 0)
    return { outcome: 'blocked', reason: 'not_accepted', tables: still }
  return {
    outcome: 'refused',
    refused: { byTable: refused, via, lastError: i.breaker.lastError },
  }
}

export type GateDeps = {
  getSummary(): Promise<PendingSummary>
  getQueueByEntity(): Promise<Record<string, number>>
  getDlq(): Promise<DlqInfo>
  /** forceSync(false, true) — push; NÃO faz pull. */
  push(): Promise<void>
  isOnline(): boolean
  breaker(): BreakerLite
  sleep(ms: number): Promise<void>
  now(): number
  maxRounds?: number
  roundDelayMs?: number
}

export type GateResult =
  | {
      status: 'clear'
      summary: PendingSummary
      dlq: DlqInfo
      queueItems: number
      rounds: number
    }
  | {
      status: 'clear_refused'
      summary: PendingSummary
      dlq: DlqInfo
      refused: RefusedInfo
      queueItems: number
      rounds: number
    }
  | {
      status: 'blocked'
      reason: GateReason
      message: string
      tables?: Record<string, number>
      summary: PendingSummary | null
      rounds: number
    }

export function gateMessage(
  reason: GateReason,
  tables?: Record<string, number>,
): string {
  const lista = tables
    ? Object.keys(tables)
        .map((t) => `${TABLE_LABEL[t] ?? t} (${tables[t]})`)
        .join(', ')
    : ''
  switch (reason) {
    case 'offline':
      return 'Sem internet. Conecte-se para enviar os dados pendentes antes de continuar.'
    case 'breaker_open':
      return 'O envio foi suspenso por alguns instantes depois de várias falhas. Aguarde cerca de 30 segundos e tente de novo.'
    case 'push_failed':
      return 'Não foi possível enviar os dados agora. Verifique a conexão e tente de novo.'
    case 'unreadable':
      return 'Não foi possível conferir os dados pendentes deste aparelho. Tente de novo.'
    case 'not_accepted':
      return `Estes registros ainda não foram enviados ao servidor: ${lista}. Tente de novo.`
  }
}

const isClean = (s: PendingSummary) =>
  blockingUnreadable(s).length === 0 && s.dirtyRecords === 0

export async function runPendingGate(d: GateDeps): Promise<GateResult> {
  const maxRounds = d.maxRounds ?? 3
  const delay = d.roundDelayMs ?? 3000
  let consecutiveRefused = 0
  let lastSummary: PendingSummary | null = null
  let lastEval: GateEvaluation | null = null

  const blocked = (
    reason: GateReason,
    round: number,
    tables?: Record<string, number>,
  ): GateResult => ({
    status: 'blocked',
    reason,
    message: gateMessage(reason, tables),
    tables,
    summary: lastSummary,
    rounds: round,
  })

  for (let round = 1; round <= maxRounds; round++) {
    let before: PendingSummary
    let dlq: DlqInfo
    try {
      before = await d.getSummary()
      dlq = await d.getDlq()
    } catch {
      return blocked('unreadable', round)
    }
    lastSummary = before
    if (isClean(before)) {
      return {
        status: 'clear',
        summary: before,
        dlq,
        queueItems: before.queuePending + before.queueInProgress,
        rounds: round,
      }
    }
    if (blockingUnreadable(before).length > 0)
      return blocked('unreadable', round)
    if (!d.isOnline()) return blocked('offline', round)

    const roundStartedAt = d.now()
    try {
      await d.push()
    } catch {
      return blocked('push_failed', round)
    }

    let after: PendingSummary
    let queueByEntity: Record<string, number>
    try {
      after = await d.getSummary()
      queueByEntity = await d.getQueueByEntity()
      dlq = await d.getDlq()
    } catch {
      return blocked('unreadable', round)
    }
    lastSummary = after
    const ev = evaluateGate({
      summary: after,
      queueByEntity,
      dlq,
      online: d.isOnline(),
      breaker: d.breaker(),
      roundStartedAt,
    })
    lastEval = ev
    const queueItems = after.queuePending + after.queueInProgress

    if (ev.outcome === 'clear') {
      return { status: 'clear', summary: after, dlq, queueItems, rounds: round }
    }
    if (ev.outcome === 'refused') {
      consecutiveRefused += 1
      if (consecutiveRefused >= 2) {
        return {
          status: 'clear_refused',
          summary: after,
          dlq,
          refused: ev.refused,
          queueItems,
          rounds: round,
        }
      }
    } else {
      consecutiveRefused = 0
      if (
        ev.reason === 'offline' ||
        ev.reason === 'breaker_open' ||
        ev.reason === 'unreadable'
      ) {
        return blocked(ev.reason, round)
      }
    }
    if (round < maxRounds) await d.sleep(delay)
  }

  if (lastEval && lastEval.outcome === 'blocked') {
    return blocked(lastEval.reason, maxRounds, lastEval.tables)
  }
  if (lastEval && lastEval.outcome === 'refused') {
    return blocked('not_accepted', maxRounds, lastEval.refused.byTable) // viu recusa uma vez só (maxRounds=1)
  }
  return blocked('not_accepted', maxRounds)
}
